// §5.4 — Snap Layouts: the flyout that opens when the maximize button is hovered.
//
// Each tile is the layout drawn to scale from its own fractions, so a layout added to the policy
// appears here correctly without anyone drawing it twice. Clicking a zone snaps the window into
// it; the window joins that layout's Snap Group, which is what Snap Assist is then about.
//
// **The close timer belongs to the frame, not to this component.** The pointer travelling from the
// button into the flyout leaves the button, and if the two kept separate timers the flyout would
// close out from under the pointer that just arrived on it.
import type { SnapLayout } from '@hangar/shared';
import { useKernel } from '@/kernel/react';

export interface SnapPickerProps {
  winId: string;
  layouts: readonly SnapLayout[];
  /** The pointer is on the flyout: cancel the frame's pending close. */
  onHold(): void;
  /** The pointer has left it: start the frame's close countdown. */
  onRelease(): void;
  /** A zone was chosen — close immediately, before the flyout's own window moves. */
  onChosen(): void;
}

export function SnapPicker({ winId, layouts, onHold, onRelease, onChosen }: SnapPickerProps) {
  const kernel = useKernel();

  const choose = (layoutId: string, zoneId: string): void => {
    onChosen();
    kernel.snap.snapTo(winId, layoutId, zoneId);
  };

  return (
    <div
      className="win11-snap-picker"
      data-testid={`snap-picker-${winId}`}
      role="menu"
      onPointerEnter={onHold}
      onPointerLeave={onRelease}
      // The flyout is inside the title bar, whose `pointerdown` starts a window drag and calls
      // `preventDefault` — which also suppresses the compatibility `click`. Every window control
      // stops propagation for the same reason; so does this.
      onPointerDown={(event) => event.stopPropagation()}
    >
      {layouts.map((layout) => (
        <div
          key={layout.id}
          className="win11-snap-layout"
          data-testid={`snap-layout-${layout.id}`}
          aria-label={layout.label}
        >
          {layout.zones.map((zone) => (
            <button
              key={zone.id}
              type="button"
              role="menuitem"
              className="win11-snap-zone"
              data-testid={`snap-zone-${zone.id}`}
              aria-label={`${layout.label}: ${zone.id}`}
              // Drawn from the same fractions the snap engine will use, so the tile cannot
              // disagree with where the window lands.
              style={{
                left: `${zone.rect.x * 100}%`,
                top: `${zone.rect.y * 100}%`,
                width: `${zone.rect.w * 100}%`,
                height: `${zone.rect.h * 100}%`,
              }}
              onClick={() => choose(layout.id, zone.id)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
